import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { toast } from '@/hooks/use-toast';
// P0-1 — the pricing copy below has to stay true on 2026-08-28.
// `VIDEO_SHIPPED` is false until mid-September, so the tier bullets
// that promise video are held with it.
import { VIDEO_SHIPPED } from '@/config/launch';

// PLANS-1 — three paid tiers.
// Old (SUB-1):   'free' | 'orbit' | 'orbit_pro' | 'orbit_business'
// New (PLANS-1): 'free' | 'orbit_starter' | 'orbit' | 'orbit_business'
//
// Naming logic:
//   • Starter → "Just starting out." (photo + video creatives, capped
//     at 15 clients / 5 active projects).
//   • Orbit   → "Run your business." (full solo, unlimited).
//   • Business → "Run your team." (unlimited seats + advanced Nova).
//
// The visible `orbit_pro` tier and the perpetual $499 License are gone
// — no live subscribers were on them when this shipped, so we drop
// cleanly. Consumers that see an unknown tier code fall back to
// `PLAN_LIMITS.free` via the || fallback below.
export type SubscriptionPlan = 'free' | 'orbit_starter' | 'orbit' | 'orbit_business';

// Which billing rails currently entitle this org. Populated by
// `check-subscription` — includes 'web' for Stripe, 'apple' for
// StoreKit, 'grant' for admin-granted access. Web UI uses this to
// disable Stripe CTAs when an Apple sub is active (Apple guidelines
// forbid a paid-outside-IAP CTA from inside the iOS app; the web
// mirror stays consistent so studios can't accidentally double-buy).
export type BillingProviderTag = 'web' | 'apple' | 'grant';

export interface SubscriptionData {
  subscribed: boolean;
  plan: SubscriptionPlan;
  subscription_end: string | null;
  billing_interval?: 'monthly' | 'yearly';
  trial_end?: string | null;
  is_trial?: boolean;
  /**
   * Providers that currently entitle this org. Empty when the org is
   * on `free`. When it contains `'apple'`, hide Stripe checkout CTAs
   * and route studios to "Manage on iPhone" instead. See spec §22.
   */
  providers?: BillingProviderTag[];
  /**
   * PAYWALL-1 — true when `check-subscription` could not be reached and
   * the row above is a client-side placeholder, not a server verdict.
   * The gate in ProtectedRoute treats "unavailable" and "free" very
   * differently: an outage falls back to the local stamp so paying
   * studios aren't locked out by a blip, while a real `free` is a wall.
   */
  unavailable?: boolean;
}

export interface SubscriptionEvent {
  id: string;
  org_id: string;
  event_type: string;
  stripe_event_id: string | null;
  amount: number | null;
  currency: string;
  status: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

// Stripe price/product IDs per plan.
// Real IDs live below. Product IDs left as `null` — not consumed
// at runtime, price IDs are the only lookup keys. The stripe-webhook
// edge fn owns the reverse lookup (Stripe price ID → plan code) —
// keep the two in sync when prices change.
//
// ─────────────────────────────────────────────────────────────────
// PLANS-5 — BUSINESS PRICE IDs ARE NOT CREATED YET.
//
// The two `orbit_business` entries below are PLACEHOLDERS, not price
// IDs. They previously held the live $149.99/$1299.99 IDs, which is
// what made the app charge $149.99 while every price we display says
// $199.99. Rather than leave a wrong-but-plausible ID in place, the
// value is now something Stripe will reject on sight.
//
// What Rennan must do, in the Stripe dashboard:
//   1. Product "Orbit Business" → add price  $199.99 USD / month
//   2. Product "Orbit Business" → add price $1999.99 USD / year
//   3. Paste the two new `price_…` IDs here, and into:
//        supabase/functions/create-checkout/index.ts   (PRICE_IDS)
//        supabase/functions/stripe-webhook/index.ts    (STRIPE_PRICE_TO_PLAN — ADD, don't replace)
//   4. Archive (do NOT delete) the old $149.99/$1299.99 prices.
//
// Full checklist, including the Nova-credits SKUs: docs/orbit-plans.md
// § "Stripe setup checklist — what Rennan must create".
//
// Until step 3 lands, `create-checkout` refuses Business checkout with
// a 503 `price_not_configured` instead of charging the old price.
// ─────────────────────────────────────────────────────────────────
export const STRIPE_IDS = {
  orbit_starter: {
    product: null as string | null,
    price_monthly: 'price_1Tyg3CLMOAxB8Md6fDgHTOZX',
    price_yearly: 'price_1Tyg3CLMOAxB8Md6MOsiPOdX',
  },
  orbit: {
    product: null as string | null,
    price_monthly: 'price_1Tyg9RLMOAxB8Md6ADWA3D78',
    price_yearly: 'price_1Tyg9pLMOAxB8Md6V3RCI35q',
  },
  orbit_business: {
    product: null as string | null,
    // $199.99/mo — NOT A REAL ID. See the block above.
    price_monthly: 'price_REPLACE_ME_BUSINESS_MONTHLY_199_99',
    // $1999.99/yr — NOT A REAL ID. See the block above.
    price_yearly: 'price_REPLACE_ME_BUSINESS_YEARLY_1999_99',
  },
} as const;

/**
 * PLANS-5 — legacy Business prices, kept for reference only.
 *
 * These are the $149.99/mo and $1299.99/yr prices the app used to
 * check out against. They must stay mapped in stripe-webhook's
 * `STRIPE_PRICE_TO_PLAN` so anyone already subscribed at the old
 * price keeps resolving to `orbit_business` — but nothing may create
 * a NEW subscription against them.
 */
export const LEGACY_BUSINESS_PRICE_IDS = {
  price_monthly_149_99: 'price_1TygBSLMOAxB8Md6of5TRuCC',
  price_yearly_1299_99: 'price_1TygBvLMOAxB8Md6w46YoQ1X',
} as const;

/**
 * True when a price ID is a PLANS-5 placeholder rather than something
 * Stripe would accept. Any UI that gates a checkout CTA on "is this
 * plan buyable yet?" should use this instead of string-matching.
 */
export const isPlaceholderPriceId = (priceId: string | null | undefined) =>
  !!priceId && priceId.startsWith('price_REPLACE_ME');

/**
 * True when the org's current subscription is billed through Apple
 * StoreKit. Used by both BillingSettings and Marketing.tsx to hide
 * Stripe checkout paths — an active Apple sub must be managed on
 * iPhone (`Settings → Subscriptions`). Empty providers → false.
 */
export const isAppleBilled = (data?: Pick<SubscriptionData, 'providers'> | null) =>
  !!data?.providers?.includes('apple');

// Fetch subscription status from Stripe via edge function
/**
 * LAPSE-1 — did this org ever pay, and is that plan now over?
 *
 * ProtectedRoute sends an org without a live plan to the plan step. For
 * a brand-new studio that is "choose your plan". For a studio whose
 * card failed in March it is "your subscription ended" — same screen,
 * a different sentence, and the difference is whether org_subscriptions
 * holds a paid row in a terminal state. Members can read their own rows
 * (SUB-1 policy), so this is one cheap select, cached like the
 * subscription itself.
 *
 * `expired` / `revoked` are the terminal states. `billing_retry` and
 * `grace_period` still count as entitled in effective_plan_for_org, so
 * an org in those never reaches the wall and never needs this.
 */
export interface LapsedSubscription {
  /** A paid plan existed and is now expired or revoked. */
  lapsed: boolean;
  plan: SubscriptionPlan | null;
  state: string | null;
  /** When the paid period ended, best available: current_period_end, else canceled_at, else the row's updated_at. */
  endedAt: string | null;
}

export const useLapsedSubscription = (orgId: string | null | undefined) => {
  return useQuery({
    queryKey: ['subscription-lapsed', orgId],
    queryFn: async (): Promise<LapsedSubscription> => {
      const none: LapsedSubscription = { lapsed: false, plan: null, state: null, endedAt: null };
      if (!orgId) return none;
      const { data, error } = await supabase
        .from('org_subscriptions')
        .select('plan, state, current_period_end, canceled_at, updated_at')
        .eq('org_id', orgId)
        .neq('plan', 'free')
        .in('state', ['expired', 'revoked'])
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error || !data) return none;
      const row = data as {
        plan: string; state: string;
        current_period_end: string | null; canceled_at: string | null; updated_at: string | null;
      };
      return {
        lapsed: true,
        plan: row.plan as SubscriptionPlan,
        state: row.state,
        endedAt: row.current_period_end ?? row.canceled_at ?? row.updated_at ?? null,
      };
    },
    enabled: !!orgId,
    staleTime: 60 * 1000,
  });
};

export const useCheckSubscription = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['subscription-status', user?.id],
    queryFn: async (): Promise<SubscriptionData> => {
      const { data, error } = await supabase.functions.invoke('check-subscription');

      if (error) {
        console.error('Error checking subscription:', error);
        return { subscribed: false, plan: 'free', subscription_end: null, unavailable: true };
      }

      return data as SubscriptionData;
    },
    enabled: !!user, // Only run when user is authenticated
    staleTime: 60 * 1000, // Cache for 1 minute
    refetchOnWindowFocus: true,
  });
};


/**
 * CHECKOUT-1 — read the JSON body an edge function attached to a
 * non-2xx response.
 *
 * supabase-js v2 hands back `data: null` and a `FunctionsHttpError`
 * whose `context` is the raw `Response` for anything outside 2xx. The
 * `{ code, message }` that create-checkout puts in a 409/503 body is
 * therefore never in `data`, and never in `error.message` either
 * ("Edge Function returned a non-2xx status code"). Every call site
 * that tried `payload?.code` on a 409 was reading null. This drains
 * the response once; the body is small and never streamed.
 */
export async function readFunctionErrorBody(
  error: unknown,
): Promise<{ code?: string; error?: string; message?: string; status?: number } | null> {
  const ctx = (error as { context?: unknown } | null)?.context;
  if (!ctx || typeof (ctx as Response).json !== 'function') return null;
  try {
    const res = ctx as Response;
    const body = (await res.clone().json()) as {
      code?: string;
      error?: string;
      message?: string;
    };
    return { ...body, code: body.code ?? body.error, status: res.status };
  } catch {
    return null;
  }
}

// Create checkout session for upgrade.
// Wave 8.1a — in Preview Studio we short-circuit before hitting
// Stripe. Never route a keynote audience to a real checkout page.
export const useCreateCheckout = () => {
  const { isDemo } = useDemoMode();
  return useMutation({
    mutationFn: async ({
      plan,
      interval,
      returnPath,
    }: {
      plan: Exclude<SubscriptionPlan, 'free'>;
      interval?: 'monthly' | 'yearly';
      /**
       * SIGNUP-1 — where Stripe sends the browser back to.
       *
       * `create-checkout` defaults to `/settings?tab=billing`, which is
       * the right answer for an upgrade started FROM the billing page
       * and the wrong one for a checkout started from onboarding — a
       * studio that just paid would land in Settings with the wizard
       * abandoned behind them. Optional, so every existing call site
       * keeps the old destination without being touched.
       */
      returnPath?: string;
    }) => {
      if (isDemo) {
        toast({
          title: 'Not available in this workspace',
          description: 'Payments will be enabled once you finish setup.',
        });
        // Return an empty string so existing callsites that
        // `window.location.href = url` can no-op without a runtime
        // error. Callers checking truthiness before redirect will
        // skip cleanly.
        return '';
      }

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan, interval: interval || 'monthly', returnPath },
      });

      // Phase 3A guardrail: create-checkout returns HTTP 409 with
      // { code: 'apple_subscription_active' } when the org already
      // has a live Apple StoreKit sub. Surface as a typed error so
      // call sites can render the "Manage on iPhone" banner instead
      // of a generic red toast.
      //
      // CHECKOUT-1 — on a non-2xx the body lives on the error, not in
      // `data` (see readFunctionErrorBody). Merge it in so the checks
      // below see the code whichever way it arrived.
      const errBody = error ? await readFunctionErrorBody(error) : null;
      const payload = ((data as object | null) ?? errBody) as
        | { url?: string; changed?: boolean; code?: string; message?: string }
        | null
        | undefined;

      // CHECKOUT-1 — a live subscriber's plan was changed in place; the
      // `url` is the caller's returnPath with `changed=true`, so every
      // existing `window.location.href = url` call site lands them
      // back where they started with the new plan already resolved.
      if (payload?.changed && payload.url) {
        return payload.url;
      }
      if (payload?.code === 'already_on_plan') {
        const err = new Error(payload.message ?? "You're already on this plan.") as Error & {
          code?: string;
        };
        err.code = 'already_on_plan';
        throw err;
      }
      if (payload?.code === 'apple_subscription_active') {
        const err = new Error(
          payload.message ??
            "You're already subscribed on Apple. Manage your subscription on iPhone → Settings → Subscriptions.",
        ) as Error & { code?: string };
        err.code = 'apple_subscription_active';
        throw err;
      }

      // PLANS-5 guardrail: create-checkout answers 503 with
      // { code: 'price_not_configured' } when the requested plan's
      // Stripe price ID is still a placeholder (Business, until the
      // $199.99/$1999.99 prices exist). Surface it as a typed error so
      // the CTA fails visibly. Nothing was charged.
      if (payload?.code === 'price_not_configured') {
        const err = new Error(
          payload.message ??
            "This plan isn't available for checkout yet. Nothing was charged.",
        ) as Error & { code?: string };
        err.code = 'price_not_configured';
        throw err;
      }

      // create-checkout answers 409 with { code: 'subscription_active' }
      // when a live Stripe subscription already exists. Plan changes go
      // through the billing portal, not a second checkout.
      if (payload?.code === 'subscription_active') {
        const err = new Error(
          payload.message ??
            'You already have an active subscription. Use Manage billing in Settings → Billing to change your plan.',
        ) as Error & { code?: string };
        err.code = 'subscription_active';
        throw err;
      }



      if (error) {
        // supabase-js wraps HTTP errors — surface the payload code
        // if the edge fn attached one.
        const wrappedCode =
          (error as { context?: { code?: string } }).context?.code;
        if (wrappedCode === 'apple_subscription_active') {
          const err = new Error(
            "You're already subscribed on Apple. Manage your subscription on iPhone → Settings → Subscriptions.",
          ) as Error & { code?: string };
          err.code = 'apple_subscription_active';
          throw err;
        }
        if (wrappedCode === 'price_not_configured') {
          const err = new Error(
            "This plan isn't available for checkout yet. Nothing was charged.",
          ) as Error & { code?: string };
          err.code = 'price_not_configured';
          throw err;
        }
        if (wrappedCode === 'subscription_active') {
          const err = new Error(
            'You already have an active subscription. Use Manage billing in Settings → Billing to change your plan.',
          ) as Error & { code?: string };
          err.code = 'subscription_active';
          throw err;
        }
        throw new Error(error.message);
      }
      if (!payload?.url) throw new Error('No checkout URL returned');

      return payload.url;
    },
  });
};

// Create customer portal session
export const useCustomerPortal = () => {
  const { isDemo } = useDemoMode();
  return useMutation({
    mutationFn: async () => {
      if (isDemo) {
        toast({
          title: 'Not available in this workspace',
          description: 'Payments will be enabled once you finish setup.',
        });
        return '';
      }

      const { data, error } = await supabase.functions.invoke('customer-portal');

      if (error) throw new Error(error.message);
      if (!data?.url) throw new Error('No portal URL returned');

      return data.url as string;
    },
  });
};

// Fetch subscription events (payment history)
export const useSubscriptionEvents = () => {
  return useQuery({
    queryKey: ['subscription-events'],
    queryFn: async (): Promise<SubscriptionEvent[]> => {
      const { data, error } = await supabase
        .from('subscription_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Error fetching subscription events:', error);
        return [];
      }

      return (data || []) as SubscriptionEvent[];
    },
  });
};

// ─────────────────────────────────────────────────────────────────────
// Plan limits — PLANS-1 shape.
//
// Nova is UNLIMITED on every paid tier. The `novaAiActionsPerMonth`
// field stays around (Infinity for paid) for backward compatibility
// with any consumer still reading it, but the visible NovaUsageBar is
// killed — the `nova_ai_actions_log` table stays for backend analytics.
//
// Starter is the only paid tier with real caps on the CRM primitives:
//   • 15 clients (`clientCap`)
//   • 5 active projects (`projectCap`)
// Every gate above Starter is Infinity — the tier is about capability
// and headroom, not counting-down features.
// ─────────────────────────────────────────────────────────────────────
export const PLAN_LIMITS = {
  free: {
    projects: 1,
    teamMembers: 1,
    clientCap: 3,
    projectCap: 1,
    storageGB: 0.1,       // 100MB — signup grace
    novaAiActionsPerMonth: 0,
    customBranding: false,
    customDomain: false,
    advancedVideoProofing: false,
    // Legacy fields retained so existing gate consumers compile
    // untouched. Free stays gated for all of them.
    storage: '100 MB',
    premiumThemes: false,
    automation: false,
    portalCustomization: false,
    apiAccess: false,
    prioritySupport: false,
    creatorAccounts: false,
    teamManagement: false,
    deliveryGalleries: false,
    workflowTemplates: false,
    emailTemplates: false,
    analytics: false,
    aiFeatures: false,
    advancedAutomation: false,
    advancedAnalytics: false,
    productionManagement: false,
    // Intake form limits
    intakeFormsEnabled: false,
    intakeFormLimit: 0,
    intakeFormCustomFields: false,
    intakeFormLogic: false,
    intakeFormTemplates: false,
    intakeFormAutomation: false as false | 'basic' | 'advanced',
    intakeFormAIInsights: false,
    intakeFormTeamRouting: false,
    intakeFormConversionAnalytics: false,
  },
  orbit_starter: {
    // "Just starting out." Real caps: 15 clients, 5 active projects.
    // No custom branding (Orbit-branded Sites, no logo swap, no
    // custom domain). PLANS-3: PHOTO GALLERIES ONLY — no video
    // delivery at all (create-mux-upload / sites-mux-upload answer
    // 402 plan_locked for the base band).
    projects: 5,
    teamMembers: 1,
    clientCap: 15,
    projectCap: 5,
    storageGB: 500,       // 500 GB
    novaAiActionsPerMonth: Infinity,
    customBranding: false,
    customDomain: false,
    advancedVideoProofing: false,
    storage: '500 GB',
    premiumThemes: false,
    automation: true,
    portalCustomization: false,
    apiAccess: false,
    prioritySupport: false,
    creatorAccounts: false,
    teamManagement: false,
    deliveryGalleries: true,
    // PLANS-1 / P1-WEB-1 — Workflows surface (#341) is killed. The
    // `workflowTemplates` flag is retained on the shape for compile
    // parity, but locked to `false` on every tier so no gate promises
    // a builder that doesn't exist. Automation rules
    // (AutomationSettings — auto-emails on inquiry/deliverable/etc.)
    // remain, gated by `automation: true` below.
    workflowTemplates: false,
    emailTemplates: true,
    analytics: true,
    aiFeatures: true,
    advancedAutomation: false,
    advancedAnalytics: false,
    productionManagement: false,
    intakeFormsEnabled: true,
    intakeFormLimit: Infinity,
    intakeFormCustomFields: true,
    intakeFormLogic: true,
    intakeFormTemplates: true,
    intakeFormAutomation: 'basic' as false | 'basic' | 'advanced',
    intakeFormAIInsights: false,
    intakeFormTeamRouting: false,
    intakeFormConversionAnalytics: false,
  },
  orbit: {
    // "Run your business." The full solo tier — unlimited clients +
    // projects, custom branding + custom domain, full video proofing
    // + delivery capped at 1080p (PLANS-3; 4K + originals live on
    // Business).
    projects: Infinity,
    teamMembers: 1,
    clientCap: Infinity,
    projectCap: Infinity,
    storageGB: 1024,      // 1 TB
    novaAiActionsPerMonth: Infinity,
    customBranding: true,
    customDomain: true,
    advancedVideoProofing: true,
    storage: '1 TB',
    premiumThemes: true,
    automation: true,
    portalCustomization: true,
    apiAccess: false,
    prioritySupport: false,
    creatorAccounts: false,
    teamManagement: false,
    deliveryGalleries: true,
    // See starter block above — Workflows surface killed (#341).
    workflowTemplates: false,
    emailTemplates: true,
    analytics: true,
    aiFeatures: true,
    // Was `true` — dropped alongside `workflowTemplates` because
    // the "advanced automation" claim was routing through the
    // deprecated Workflows builder. Automation rules (basic
    // triggers → email/team notify) still live at `automation: true`.
    advancedAutomation: false,
    advancedAnalytics: false,
    productionManagement: false,
    intakeFormsEnabled: true,
    intakeFormLimit: Infinity,
    intakeFormCustomFields: true,
    intakeFormLogic: true,
    intakeFormTemplates: true,
    intakeFormAutomation: 'advanced' as false | 'basic' | 'advanced',
    intakeFormAIInsights: false,
    intakeFormTeamRouting: false,
    intakeFormConversionAnalytics: true,
  },
  orbit_business: {
    // "Run your team." PLANS-3 repositioning: the tier serious
    // videographers buy — 4K streaming, original-file downloads
    // (get-video-master), high streaming allowance — plus everything
    // Business already had: unlimited team + storage, team +
    // production management, brand removal, custom email domains,
    // advanced Nova Design, priority support, API access.
    projects: Infinity,
    teamMembers: Infinity,
    clientCap: Infinity,
    projectCap: Infinity,
    storageGB: Infinity,  // unlimited (reasonable-use policy in copy)
    novaAiActionsPerMonth: Infinity,
    customBranding: true,
    customDomain: true,
    advancedVideoProofing: true,
    storage: 'Unlimited',
    premiumThemes: true,
    automation: true,
    portalCustomization: true,
    apiAccess: true,
    prioritySupport: true,
    creatorAccounts: true,
    teamManagement: true,
    deliveryGalleries: true,
    // See starter block — Workflows surface killed (#341).
    workflowTemplates: false,
    emailTemplates: true,
    analytics: true,
    aiFeatures: true,
    // See orbit block — `advancedAutomation` gated a killed surface.
    advancedAutomation: false,
    advancedAnalytics: true,
    productionManagement: true,
    intakeFormsEnabled: true,
    intakeFormLimit: Infinity,
    intakeFormCustomFields: true,
    intakeFormLogic: true,
    intakeFormTemplates: true,
    intakeFormAutomation: 'advanced' as false | 'basic' | 'advanced',
    intakeFormAIInsights: true,
    intakeFormTeamRouting: true,
    intakeFormConversionAnalytics: true,
  },
} as const;

// Plan pricing (web / Stripe list price). Psychological pricing —
// every SKU ends in .99. Yearly prices matched to Apple App Store
// subscription tiers so iOS displayPrice + Stripe list price stay
// in lockstep (avoids "$1490 on Stripe / $1499.99 on Apple" jank):
//   Starter  $49.99/mo  · $499.99/yr
//   Orbit    $69.99/mo  · $699.99/yr
//   Business $199.99/mo · $1999.99/yr
// PLANS-3 — Business repriced $149.99 → $199.99 and repositioned as
// the tier serious videographers buy (4K streaming, original-file
// downloads, high streaming allowance). Yearly moves to $1999.99 to
// keep the ~17% annual discount the toggle promises and stay on an
// Apple price point.
// Stored as decimals (not cents) because Marketing.tsx + BillingSettings
// display these directly with a "$" prefix. adminAccounts.ts + MRR
// calcs multiply by 100 to get cents.
export const PLAN_PRICING = {
  free: { monthly: 0, yearly: null as number | null },
  orbit_starter: { monthly: 49.99, yearly: 499.99 },
  orbit: { monthly: 69.99, yearly: 699.99 },
  orbit_business: { monthly: 199.99, yearly: 1999.99 },
} as const;

// Alias for callers that expect the newer name from the plan.
export const PLAN_PRICES = PLAN_PRICING;

// ─────────────────────────────────────────────────────────────────────
// LAUNCH-2 — THERE IS NOW A 7-DAY TRIAL, AND IT IS REAL —
// BUT IT IS NOT ANNOUNCED YET. See `TRIAL_SHIPPED` in
// src/config/launch.ts: the copy and the mechanism deploy separately
// (git push vs `supabase functions deploy`), so every trial sentence
// is behind that flag until the function is live. Deploy the function
// first, then flip it.
//
// Configured in `supabase/functions/create-checkout/index.ts`:
// `trial_period_days: 7`, `payment_method_collection: 'always'`, and
// `trial_settings.end_behavior.missing_payment_method: 'cancel'`. Card
// up front, auto-charge on day 7. `TRIAL_DAYS` / `TRIAL_MESSAGE` in
// `src/config/launch.ts` are the single source of the copy.
//
// THE TRIAL IS NOT THE FREE TIER. `PLAN_FEATURES.free` below is
// view-only — a showroom. The trial is 7 days of a PAID plan. Any
// surface that blurs those two is reintroducing the bug this comment
// block was written about, which is:
//
// P0-1 (HISTORY — the 14-day claim that was deleted in August).
// `TRIAL_CONFIG` (14 days, 6-month
// cooldown) was aspirational config that nothing ever enforced:
//
//   • `create-checkout/index.ts` builds every
//     `stripe.checkout.sessions.create({...})` with no
//     `trial_period_days` and no `subscription_data` — grep it, the
//     keys aren't there. Stripe therefore charges on day one.
//   • All six StoreKit products carry `"introductoryOffer": null`
//     (`ios/Orbit/Orbit/Orbit.storekit`), and the README beside it
//     says the intro offer was deliberately skipped.
//   • Nothing anywhere read `cooldownMonths`.
//
// The only consumer was the onboarding plan step, which rendered
// "All plans include a 14-day free trial. No credit card required."
// directly above a button that opens a Stripe card form. Two false
// claims on the payment screen. The constant is deleted rather than
// zeroed so no future surface can resurrect the claim by reading it.
//
// `SubscriptionData.is_trial` / `trial_end` stay on the type — they
// are passed straight through from Stripe by `check-subscription`,
// so if a trial is ever configured ON A PRICE in the Stripe
// dashboard the UI will reflect reality without a code change.
//
// Step 1 of the two below is DONE (LAUNCH-2, at 7 days rather than
// 14). STEP 2 IS NOT, and is the one open contradiction in the trial:
//   1. DONE — `create-checkout/index.ts` now sets
//      `subscription_data: { trial_period_days: 7 }`.
//   2. OPEN — App Store Connect → add a free-trial introductory offer
//      to the subscription group, and mirror it in `Orbit.storekit`
//      (all six products still carry `"introductoryOffer": null`).
//      Nothing ships a contradiction TODAY because the iPhone and
//      iPad apps are held to later this fall (`IOS_SHIPPED` in
//      config/launch.ts) — but this must land before they do, or the
//      apps will charge on day one while the website promises seven
//      days free.
// ─────────────────────────────────────────────────────────────────────

// Plan taglines — surface on marketing + Settings → Billing.
export const PLAN_TAGLINES = {
  free: '',
  orbit_starter: 'Just starting out.',
  orbit: 'Run your business.',
  orbit_business: 'Run your team.',
} as const;

// Plan descriptions — long-form single-liner. Used by UpgradeModal.
export const PLAN_DESCRIPTIONS = {
  free: 'Explore Orbit before subscribing.',
  orbit_starter:
    'Everything you need to book, deliver, and get paid — up to 15 clients and 5 active projects.',
  orbit: 'The whole toolkit for a solo business — unlimited clients, custom domain, custom branding.',
  orbit_business:
    'For studios that deliver serious video — 4K, original files, your team, your brand.',
} as const;

// Plan features for display — high-signal bullets per tier. Copy
// tuned to outcome, not spec-sheet (see feedback_marketing_voice memory).
//
// P0-1 — two honesty edits, both restored by one flag:
//   • Free's third bullet said "14-day trial available". It isn't.
//     Replaced with what signing up actually gets you.
//   • Orbit's video bullet is held behind `VIDEO_SHIPPED` — on
//     2026-08-28 no video surface renders anywhere in the product
//     (LAUNCH-1), so selling 1080p delivery on the tier a studio can
//     buy today is a purchase-decision claim we can't keep.
// Business's bullets are left intact: the tier isn't on sale
// (`BUSINESS_ON_SALE`), and when it returns in September its video
// bullets are true again.
export const PLAN_FEATURES: Record<SubscriptionPlan, readonly string[]> = {
  free: [
    'View-only dashboard access',
    'Explore the interface',
    // LAUNCH-2 — kept verbatim. This is the line that keeps "free to
    // look around" and "7 days free on a paid plan" from reading as
    // the same offer.
    'No card needed to look around',
  ],
  orbit_starter: [
    'Up to 15 clients + 5 active projects',
    'Photo galleries, client portal, and payments',
    'Nova unlimited',
    '500 GB storage',
  ],
  orbit: [
    'Unlimited clients + projects',
    ...(VIDEO_SHIPPED ? ['Video proofing + delivery in 1080p'] : []),
    'Custom domain + logo across app & emails',
    'Nova unlimited',
    '1 TB storage',
  ],
  orbit_business: [
    '4K streaming + original-file downloads',
    'Timestamped video review, unlimited',
    'Your brand only — no “made with Orbit”',
    'Send from your own email domain',
    'Unlimited team seats + production management',
    'Unlimited storage · priority support',
  ],
};

// Plan display names.
export const PLAN_LABELS = {
  free: 'Free',
  orbit_starter: 'Orbit Starter',
  orbit: 'Orbit',
  orbit_business: 'Orbit Business',
} as const;

// Legacy alias — some pre-SUB-1 imports still call this `PLAN_NAMES`.
export const PLAN_NAMES = PLAN_LABELS;
